import React, { useState, useEffect } from 'react'
import { Popover, Button, IconButton, Icon, Flex } from '@ui'
import ClearableSelectWrapper from './ClearableSelectWrapper'
import moment from 'moment'
import isNull from 'lodash/isNull'
import { DateRangePicker, IDateRangeShortcut, DateRange } from '@blueprintjs/datetime'
import { IUIButtonProps } from '@ui/buttons/Button'
import PrettyDate from '../PrettyDate'

const now = moment()
const todayStart = now.clone().startOf('day').add(1, 'minute').toDate()
const todayEnd = now.clone().endOf('day').subtract(1, 'minute').toDate()

export const options: {
  past: IDateRangeShortcut[]
  future: IDateRangeShortcut[]
} = {
  past: [
    {
      label: 'Last week',
      dateRange: [now.clone().startOf('day').subtract(1, 'week').toDate(), todayEnd],
    },
    {
      label: 'Last month',
      dateRange: [now.clone().startOf('day').subtract(1, 'month').toDate(), todayEnd],
    },
    {
      label: 'Last 3 months',
      dateRange: [now.clone().startOf('day').subtract(3, 'month').toDate(), todayEnd],
    },
    {
      label: 'Last 6 months',
      dateRange: [now.clone().startOf('day').subtract(6, 'month').toDate(), todayEnd],
    },
    {
      label: 'Last year',
      dateRange: [now.clone().subtract(1, 'year').toDate(), todayEnd],
    },
  ],
  future: [
    {
      label: 'Next week',
      dateRange: [todayStart, now.clone().endOf('day').add(1, 'week').toDate()],
    },
    {
      label: 'Next month',
      dateRange: [todayStart, now.clone().endOf('day').add(1, 'month').toDate()],
    },
    {
      label: 'Next 3 months',
      dateRange: [todayStart, now.clone().endOf('day').add(3, 'month').toDate()],
    },
    {
      label: 'Next 6 months',
      dateRange: [todayStart, now.clone().endOf('day').add(6, 'month').toDate()],
    },
    {
      label: 'Next year',
      dateRange: [todayStart, now.clone().endOf('day').add(1, 'year').toDate()],
    },
  ],
}

interface DateSelectProps {
  buttonProps?: IUIButtonProps
  past?: boolean
  future?: boolean
  value: DateRange
  clearable?: boolean
  shortcuts?: IDateRangeShortcut[]
  onChange: (dateRange: DateRange | null) => void
}

export default function DateSelect({
  buttonProps,
  value,
  clearable,
  past,
  future,
  shortcuts,
  onChange,
}: DateSelectProps) {
  const [range, setRange] = useState(value)

  const handleOnchangeRange = (selectedDates: DateRange) => {
    setRange((prevDateRange) => {
      const isSelectedDatesEmpty = selectedDates.every(isNull)

      if (isSelectedDatesEmpty) {
        return prevDateRange
      }

      return selectedDates
    })
  }

  useEffect(() => {
    const valueCount = range.filter((d) => d).length
    // Only trigger on change when range is either has start and end or empty
    if (valueCount === 2 || valueCount === 0) {
      onChange(range)
    }
    // eslint-disable-next-line
  }, [range])

  useEffect(() => {
    setRange(value)
    // eslint-disable-next-line
  }, [value[0]?.getDay(), value[1]?.getDay()])

  let customShortcuts: IDateRangeShortcut[] = []

  if (past) {
    customShortcuts = [...options.past]
  }

  if (future) {
    customShortcuts = [...customShortcuts, ...options.future]
  }

  if (shortcuts) {
    customShortcuts = shortcuts
  }

  const hasValue = !!range.filter((d) => d).length
  let minDate = now.clone().startOf('day').subtract(20, 'years').toDate()

  let maxDate = now.clone().endOf('day').add(20, 'years').endOf('day').toDate()

  if (!past) {
    minDate = now.clone().startOf('day').toDate()
  }

  if (!future) {
    maxDate = now.clone().endOf('day').toDate()
  }

  return (
    <ClearableSelectWrapper>
      <Popover
        position="bottom-left"
        content={
          <DateRangePicker
            allowSingleDayRange
            shortcuts={customShortcuts}
            onChange={handleOnchangeRange}
            value={range}
            minDate={minDate}
            maxDate={maxDate}
          />
        }>
        <Button
          {...buttonProps}
          text={<RangeLabel dateRange={value} />}
          rightIconName={hasValue ? undefined : 'chevron-down'}
        />
      </Popover>
      {hasValue && clearable && (
        <IconButton minimal iconName="times" tooltip="Clear" onClick={() => onChange(null)} />
      )}
    </ClearableSelectWrapper>
  )
}

function RangeLabel({ dateRange }: { dateRange: DateRange }) {
  const start = dateRange[0]
  const end = dateRange[1]

  if (!start && !end) {
    return <span>Select</span>
  }

  return (
    <Flex>
      {start ? <PrettyDate value={start} /> : 'No date'}
      <Icon name="long-arrow-right" style={{ margin: '0 8px' }} />
      {end ? <PrettyDate value={end} /> : 'No date'}
    </Flex>
  )
}
